import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "@/lib/pnp";
import { Rise, RevealWords } from "./Reveal";

function GalleryItem({ src, index, title, tag, align = "left" }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    e.currentTarget.style.setProperty("--x", `${x}%`);
    e.currentTarget.style.setProperty("--y", `${y}%`);
  };

  return (
    <Rise delay={index * 0.05}>
      <figure
        ref={ref}
        onMouseMove={handleMove}
        data-testid={`gallery-item-${index}`}
        className={`pp-spotlight relative overflow-hidden rounded-md ring-1 ring-[#DFEBE4] ${
          align === "right" ? "md:ml-auto" : ""
        }`}
        style={{ aspectRatio: "4/5" }}
      >
        <motion.img
          src={src}
          alt={title}
          style={{ y }}
          className="absolute inset-0 h-[120%] w-full object-cover"
          loading="lazy"
        />
        <figcaption className="absolute left-4 bottom-4 right-4 flex items-end justify-between text-white z-10">
          <div>
            <div className="font-mono-pp text-[10px] uppercase tracking-[0.22em] opacity-90">/{String(index + 1).padStart(2, "0")}</div>
            <div className="font-display font-semibold text-lg leading-tight mt-1">{title}</div>
          </div>
          <span className="font-mono-pp text-[10px] uppercase tracking-[0.22em] opacity-90">{tag}</span>
        </figcaption>
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent pointer-events-none" />
      </figure>
    </Rise>
  );
}

export default function Gallery() {
  const items = [
    { src: ASSETS.poolTerrace, title: "Poolterrasse — Sommeransicht", tag: "Terrassenpflege", align: "left" },
    { src: ASSETS.bench, title: "Bushaltestelle Bänkelchen", tag: "Öffentliches Grün", align: "right" },
    { src: ASSETS.hedgeAlley, title: "Kirschlorbeer-Allee", tag: "Heckenschnitt", align: "left" },
  ];

  return (
    <section
      id="galerie"
      data-testid="gallery-section"
      className="relative bg-white py-24 md:py-40"
    >
      <div className="px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="font-mono-pp text-[11px] uppercase tracking-[0.24em] text-[#4F6858] mb-6">
              Galerie · Ausgewählte Projekte
            </div>
            <h2
              className="font-display font-extrabold text-[#082E19] leading-[0.92] tracking-tighter max-w-3xl"
              style={{ fontSize: "clamp(2.25rem, 5.4vw, 4.5rem)" }}
            >
              <RevealWords text="Arbeiten, auf die wir stolz sind." />
            </h2>
          </div>
          <div className="font-mono-pp text-[11px] uppercase tracking-[0.24em] text-[#4F6858]">
            2024 — 2025
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-7">
            <GalleryItem {...items[0]} index={0} />
          </div>
          <div className="col-span-12 md:col-span-5 md:mt-24">
            <GalleryItem {...items[1]} index={1} />
          </div>
          <div className="col-span-12 md:col-span-8 md:col-start-3">
            <GalleryItem {...items[2]} index={2} />
          </div>
        </div>
      </div>
    </section>
  );
}
